import { DecimalPipe, TitleCasePipe } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import {
  APP_INITIALIZER,
  CUSTOM_ELEMENTS_SCHEMA,
  Injector,
  NgModule
} from '@angular/core';
import { GoogleMapsModule } from '@angular/google-maps';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouteReuseStrategy } from '@angular/router';
import { MapfreFooterSinisterComponent } from '@components/mapfre-footer-sinister/mapfre-footer.component';
import { CoreModule } from '@core/core.module';
import {
  Environment, FirebaseRemoteConfigService, HTTP_CACHE_INTERCEPTOR_DURATION_MINS,
  MMobileService
} from '@core/services/common';
import { AnalyticsContext } from '@core/services/core/analytics/analytics-context';
import { AnalyticsInitializer } from '@core/services/core/analytics/analytics-initializer';
import { GoogleMapsService } from '@core/services/core/google-maps.service';
import { InjectorService } from '@core/services/core/injector.service';
import { LogService } from '@core/services/core/log.service';
import { MMobileHelperService } from '@core/services/core/mmobile-helper.service';
import { NoDirectAccessGuard } from '@core/services/guards/no-direct-access-guard';
import { Badge } from '@ionic-native/badge/ngx';
import { Calendar } from '@ionic-native/calendar/ngx';
import { Camera } from '@ionic-native/camera/ngx';
import { Clipboard } from '@ionic-native/clipboard/ngx';
import { Device } from '@ionic-native/device/ngx';
import { FileOpener } from '@ionic-native/file-opener/ngx';
import { File } from '@ionic-native/file/ngx';
import { FingerprintAIO } from '@ionic-native/fingerprint-aio/ngx';
import { FirebaseX } from '@ionic-native/firebase-x/ngx';
import { Geolocation } from '@ionic-native/geolocation/ngx';
import { InAppBrowser } from '@ionic-native/in-app-browser/ngx';
import { Keyboard } from '@ionic-native/keyboard/ngx';
import { LocalNotifications } from '@ionic-native/local-notifications/ngx';
import { OpenNativeSettings } from '@ionic-native/open-native-settings/ngx';
import { SafariViewController } from '@ionic-native/safari-view-controller/ngx';
import { SecureStorage } from '@ionic-native/secure-storage/ngx';
import { SmsRetriever } from '@ionic-native/sms-retriever/ngx';
import { SplashScreen } from '@ionic-native/splash-screen/ngx';
import { StatusBar } from '@ionic-native/status-bar/ngx';
import { IonicModule, IonicRouteStrategy } from '@ionic/angular';
import { IonicStorageModule } from '@ionic/storage';
import { EnvironmentConfigService as VehicleAccidentsConfigService, VehicleAccidentsModule } from '@mapfre/vehicle-accidents';
import { isProduction } from '@utils/utils';
import { IonBottomDrawerModule } from 'ion-bottom-drawer';
import { NgxImageCompressService } from 'ngx-image-compress';
import { filter, take } from 'rxjs/operators';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { FormatMomentDatePipe } from './shared/pipes/format-moment-date.pipe';
import { SharedModule } from './shared/shared.module';

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    SharedModule,
    IonicModule.forRoot(),
    CoreModule,
    VehicleAccidentsModule.forRoot({
      backPage: {
        homePath: 'client-area/home',
        productPath: ''
      },
      serviceImplementation: {
        analyticsInitializerService: AnalyticsInitializer,
        analyticsContextService: AnalyticsContext
      },
      componentImplementation: {
        footerComponent: MapfreFooterSinisterComponent
      }
    }),
    AppRoutingModule,
    HttpClientModule,
    BrowserAnimationsModule,
    IonBottomDrawerModule,
    IonicStorageModule.forRoot({ name: 'mapfre-mets' }),
    GoogleMapsModule,
  ],
  providers: [
    Device,
    File,
    FileOpener,
    StatusBar,
    SplashScreen,
    Keyboard,
    InAppBrowser,
    MMobileService,
    SafariViewController,
    SecureStorage,
    Geolocation,
    SmsRetriever,
    FingerprintAIO,
    LogService,
    TitleCasePipe,
    FirebaseX,
    LocalNotifications,
    Badge,
    Camera,
    Calendar,
    Clipboard,
    OpenNativeSettings,
    NgxImageCompressService,
    FormatMomentDatePipe,
    DecimalPipe,
    {
      provide: APP_INITIALIZER,
      useFactory: initializer,
      multi: true,
      deps: [
        MMobileHelperService,
        SplashScreen,
        Environment,
        FirebaseRemoteConfigService,
        NoDirectAccessGuard
      ]
    },
    {
      provide: APP_INITIALIZER,
      useFactory: injectorFactory,
      deps: [InjectorService],
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: googleMapsConfigFactory,
      deps: [Environment, GoogleMapsService],
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: vehicleAccidentsEnvConfigInitializer,
      multi: true,
      deps: [
        MMobileHelperService,
        VehicleAccidentsConfigService,
        Environment
      ]
    },
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    { provide: HTTP_CACHE_INTERCEPTOR_DURATION_MINS, useValue: 5 }
  ],
  bootstrap: [AppComponent],
  schemas: [ CUSTOM_ELEMENTS_SCHEMA ]
})
export class AppModule {
  static injector: Injector;

  constructor(injector: Injector) {
    AppModule.injector = injector;
  }
}

export function initializer(
  mmobileHelper: MMobileHelperService,
  splashScreen: SplashScreen,
  env: Environment,
  firebaseRemoteConfig: FirebaseRemoteConfigService
) {
  env
    .hasEnvironmentPrompted$()
    .pipe(filter(Boolean), take(1))
    .subscribe(() => splashScreen.hide());

  return async () => {
    await env.ready();
    await mmobileHelper.initMMobile();
    await firebaseRemoteConfig.init();
    mmobileHelper.notifyMmobileIsReady();
  };
}


export function injectorFactory(_: InjectorService) {
  const noop = () => {};
  return noop;
}

export function vehicleAccidentsEnvConfigInitializer(
  mmobileHelper: MMobileHelperService,
  envConfig: VehicleAccidentsConfigService,
  env: Environment
) {
  return async () => {
    env.ready().then(() => {
      const { mmobileConfigVersion, versionEnv } = mmobileHelper.envConfig;
      envConfig.setEnvConfig({
        mmobileUrl: mmobileHelper.envConfig.mmobileUrl,
        mmobileAppName: mmobileHelper.envConfig.mmobileAppName,
        mmobileConfigVersion,
        versionEnv,
        isProductionEnv: isProduction()
      });
    });
  };
}

export function googleMapsConfigFactory(
  env: Environment,
  googleMapsService: GoogleMapsService
) {
  return () => {
    env.ready().then(() => {
      googleMapsService.ready();
    });
  };
}
